'use client';

import FreestarAdSlot from '@freestar/pubfig-adslot-react-component';
import clsx from 'clsx';
import { usePathname } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

import { isRegisteredAdFreePath } from '../utils/ad-free-registry';
import { isWebview } from '../utils/helpers';
import { FreestarPlacementId, Publisher } from '../utils/types';

interface CustomFreestarAdProps {
	placementId: FreestarPlacementId;
	publisher: Publisher;
	place?: number;
	sizes?: string;
	showFromWidth?: number;
	hideFromWidth?: number;
	containerClassname?: string;
}

export const CustomFreestarAd = ({ placementId, publisher, place, containerClassname, showFromWidth, hideFromWidth }: CustomFreestarAdProps) => {
	const [shouldRender, setShouldRender] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);
	// Re-evaluated on every client-side navigation, which is the point: the server component that
	// rendered this slot may sit in a shared layout that Next never re-renders. See ad-free-registry.
	const pathname = usePathname();

	const slotId = place !== undefined ? `${placementId}_${place}` : placementId;

	useEffect(() => {
		if (!isWebview() && (!showFromWidth || showFromWidth <= window.innerWidth) && (!hideFromWidth || window.innerWidth < hideFromWidth)) {
			setShouldRender(true);
		}
	}, []);

	useEffect(() => {
		if (!containerRef.current) return;

		const observer = new MutationObserver(() => {
			const iframes = containerRef.current?.querySelectorAll('iframe');
			iframes?.forEach((iframe: HTMLIFrameElement) => {
				iframe.setAttribute('tabindex', '-1');
			});
		});

		observer.observe(containerRef.current, {
			childList: true,
			subtree: true
		});

		return () => observer.disconnect();
	}, [shouldRender]);

	if (isRegisteredAdFreePath(pathname)) return null;

	if (!shouldRender) return null;

	return (
		<div ref={containerRef} className={clsx('w-full flex justify-center', containerClassname)}>
			<FreestarAdSlot publisher={publisher} placementName={placementId} slotId={slotId} classList={[placementId]} />
		</div>
	);
};
